import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Sidenavaction } from '../../../../assets/js/application/sidenavaction';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import * as moment from 'moment';
import { SpeciesTypeService } from '../../../core/services/species-type.service';
import SpeciesTypeModel from '../../../core/models/species-type.model';
import { CultureCategoryService } from '../../../core/services/culture-category.service';
import CultureCategoryModel from '../../../core/models/culture-category.model';
import { ToastrService } from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/core/services/common-methods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/core/services/previous-route.service';
import { BatchLoader } from "../../../core/utilities/batch-loader.util";

/**
 * SpeciesTypeComponent
 *
 * This component is responsible for managing Species Types within the Pond Management module.
 * It provides a user interface for administrators to view, create, update, and search for species types.
 * The component implements infinite scrolling for efficient data display and uses reactive forms for data entry.
 *
 * Key Responsibilities:
 * - Fetch and display a list of all species types.
 * - Provide search functionality to filter species types by name.
 * - Manage the creation of new species types with validation.
 * - Handle updates to existing species types.
 * - Implement infinite scrolling using BatchLoader to handle large datasets.
 * - Manage UI state for loading indicators, form visibility, and submission status.
 *
 * Features:
 * - **CRUD Operations**: Create and Update species types.
 * - **Real-time Search**: Filter list by species type name.
 * - **Infinite Scroll**: Load data in batches to improve performance.
 * - **Form Validation**: Ensures species type names follow specific patterns (alphanumeric, spaces).
 * - **Date Formatting**: Formats 'updatedAt' timestamps for display.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`).
 * 2. Fetches all culture categories from `CultureCategoryService` for dropdown selection.
 * 3. Fetches all species types from `SpeciesTypeService`.
 * 4. Formats dates using `moment`.
 * 5. Initializes `BatchLoader` with the full dataset.
 * 6. User interactions (scroll, search, add/edit) trigger state updates and service calls.
 * 7. `addUpdateSpeciesType` sends data to backend and refreshes the list on success.
 *
 * @component
 * @selector app-species-type
 * @example
 * <app-species-type></app-species-type>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-species-type',
  templateUrl: './species-type.component.html',
  styleUrls: ['./species-type.component.scss']
})
export class SpeciesTypeComponent implements OnInit, OnDestroy {
  /**
   * Array of available culture categories for selection in the form.
   * @type {CultureCategoryModel[]}
   * @default []
   */
  public cultureCategories: CultureCategoryModel[] = [];

  /**
   * Array holding all fetched species types from the backend.
   * Acts as the source of truth for the list before filtering.
   * @type {SpeciesTypeModel[]}
   * @default []
   */
  public speciesTypes: SpeciesTypeModel[] = [];

  /**
   * Array holding the currently displayed species types.
   * This list is populated by the BatchLoader (subset of speciesTypes or filteredSpeciesTypes).
   * @type {SpeciesTypeModel[]}
   * @default []
   */
  public speciesTypesList: SpeciesTypeModel[] = [];

  /**
   * Array holding species types that match the current search criteria.
   * Used as the source for BatchLoader when filtering is active.
   * @type {SpeciesTypeModel[]}
   * @default []
   */
  public filteredSpeciesTypes: SpeciesTypeModel[] = [];

  /**
   * Model for the current species type being added or edited.
   * Bound to the form for data manipulation.
   * @type {SpeciesTypeModel}
   */
  public speciesType: SpeciesTypeModel = new SpeciesTypeModel();

  /**
   * Current search query string entered by the user.
   * @type {string}
   */
  public search: string;

  /**
   * Flag indicating if the form is in 'Create' mode.
   * @type {boolean}
   */
  public submitButton: boolean;

  /**
   * Flag indicating if the form is in 'Update' mode.
   * @type {boolean}
   */
  public updateButton: boolean;

  /**
   * Reactive form group for species type details (culture_category_id, name, abbreviation, description).
   * @type {FormGroup}
   */
  public registerForm: FormGroup;

  /**
   * Flag indicating if the form has been submitted.
   * Used to trigger validation error display in the UI.
   * @type {boolean}
   * @default false
   */
  public submitted = false;

  /**
   * Flag to prevent double clicks on submit actions.
   * @type {boolean}
   */
  public speciesTypeDoubleClick: boolean;

  /**
   * Flag indicating if no data is available for display.
   * @type {boolean}
   * @default false
   */
  public NoDataMessage: boolean = false;

  /**
   * Flag to trigger clearing of the search input field.
   * @type {boolean}
   * @default false
   */
  public clearSearchInput: boolean = false;

  /**
   * Flag to control the visibility of the main page loader.
   * @type {boolean}
   * @default false
   */
  public enableDisablePageLoader: boolean = false;

  /**
   * Flag to control the visibility of the list loader spinner during infinite scroll.
   * @type {boolean}
   * @default false
   */
  public listLoader = false;              // Show listLoader spinner

  /**
   * Tracks the number of items currently loaded by the batch loader.
   * @type {number}
   * @default 0
   */
  public startIndex = 0;             // Tracks how much has been loaded

  /**
   * Flag indicating if a search filter is currently active.
   * @type {boolean}
   * @default false
   */
  public isFiltering: boolean = false;

  /**
   * Flag indicating if the list is empty (no data or no search results).
   * @type {boolean}
   * @default false
   */
  public noList: boolean = false;

  /**
   * Timeout reference for search input debouncing.
   * @type {any}
   */
  searchTimeout: any;

  /**
   * Count of fields in the form, used for layout adjustments.
   * @type {number}
   * @default 0
   */
  public formFieldCount: number = 0;

  /**
   * Flag to toggle the visibility of the add/edit form.
   * @type {boolean}
   * @default false
   */
  public showForm: boolean = false;

  /**
   * Constructor for SpeciesTypeComponent
   *
   * Initializes the component, injects required services, and sets up the reactive form.
   *
   * Form Validation Rules:
   * - culture_category_id: Required.
   * - name: Required, Min 3, Max 20, Pattern (alphabetic, spaces).
   * - abbreviation: Required, Max 10, Pattern (alphanumeric).
   * - description: Max 200 characters.
   *
   * @constructor
   * @param {ToastrService} toasterService - Service for displaying toast notifications.
   * @service ToastrService
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @service Sidenavaction
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @service FormBuilder
   * @param {SpeciesTypeService} speciesTypeService - Service for Species Type API operations.
   * @service SpeciesTypeService
   * @param {CultureCategoryService} cultureCategoryService - Service for Culture Category API operations.
   * @service CultureCategoryService
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods and error handling.
   * @service CommonMethodsService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   */
  constructor(private toasterService: ToastrService, public sidenavaction: Sidenavaction, public formBuilder: FormBuilder, public speciesTypeService: SpeciesTypeService, public cultureCategoryService: CultureCategoryService, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.speciesType = new SpeciesTypeModel();
    /*form validation*/
    this.registerForm = this.formBuilder.group({
      culture_category_id: ['', [Validators.required]],
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20), Validators.pattern('^[a-zA-Z ]*$')]],
      abbreviation: ['', [Validators.required, Validators.maxLength(10), Validators.pattern('^[a-zA-Z0-9]*$')]],
      description: ['', [Validators.maxLength(200)]]
    });
    // Calculate field count after form is built // new
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /**
   * Utility for handling batch loading of data for infinite scroll.
   * @type {BatchLoader<any>}
   * @private
   */
  private batchLoader: BatchLoader<any>;

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Sets up side navigation.
   * 2. Resets component state and form.
   * 3. Tracks the current route.
   * 4. Fetches all culture categories for dropdowns.
   * 5. Fetches all species types from the backend.
   * 6. Formats the 'updatedAt' date for each species type.
   * 7. Initializes the batch loader and loads the first batch of data.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook
   */
  public async ngOnInit() {
    this.sidenavaction.x_navigation();
    window.scrollTo(0, 0);
    this.enableDisablePageLoader = true;
    this.clear();
    this.speciesTypes = [];
    this.speciesTypesList = [];
    this.filteredSpeciesTypes = [];
    this.isFiltering = false;
    this.search = '';
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    try {
      this.cultureCategories = await this.cultureCategoryService.getAllCultureCategories();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
    await this.getSpeciesType();
  }

  /**
   * Fetches all species types from the backend.
   * Formats dates and initializes the batch loader.
   *
   * @async
   * @method getSpeciesType
   * @returns {Promise<void>}
   * @throws {Error} If the service call fails.
   */
  public async getSpeciesType() {
    try {
      this.speciesTypes = await this.speciesTypeService.getSpeciesTypesWithCC();
      if (this.speciesTypes.length > 0) {
        this.speciesTypes.forEach(eachSpeciesType => {
          eachSpeciesType.updatedAt = JSON.parse(JSON.stringify(moment(eachSpeciesType.updatedAt).format('DD-MMM-YYYY hh:mm:ss A')));
        });
      }
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.enableDisablePageLoader = false;
    }
  }

  /**
   * Host listener for window scroll events.
   * Triggers the check for loading the next batch of data when scrolling near the bottom.
   *
   * @method onScroll
   * @returns {void}
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * Timeout reference for scroll event debouncing.
   * @type {any}
   * @private
   */
  private scrollTimeout: any;

  /**
   * Checks scroll position and loads next batch if near bottom.
   * Debounced to prevent excessive calls.
   *
   * @method checkScrollAndLoad
   * @private
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * Initializes the BatchLoader instance.
   * Configures the source list (filtered or full) and the callback to update the displayed list.
   *
   * @method initializeBatchLoader
   * @private
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredSpeciesTypes : this.speciesTypes,
      (updatedList) => {
        this.speciesTypesList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * Cleans up resources:
   * - Cancels ongoing HTTP requests for SpeciesTypeService and CultureCategoryService.
   * - Destroys and resets the batch loader.
   *
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook
   */
  public ngOnDestroy(): void {
    this.speciesTypeService.cancelOnGoingHttpRequests();
    this.cultureCategoryService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader.reset();
  }

  /**
   * Getter for easy access to form controls in the template.
   *
   * @method speciesTypeFormValidation
   * @returns {AbstractControl} Form controls
   */
  public get speciesTypeFormValidation() {
    return this.registerForm.controls;
  }

  /**
   * Filters the species type list based on the search query.
   *
   * - Filters by name (case-insensitive).
   * - Updates `filteredSpeciesTypes`.
   * - Resets the batch loader to display filtered results.
   *
   * @async
   * @method applySearch
   * @returns {Promise<void>}
   * @side-effect Updates `filteredSpeciesTypes`, `speciesTypesList`, `noList`, `isFiltering`.
   */
  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.speciesTypes.filter(route =>
        route.name?.toLowerCase().includes(query)
      )
      : this.speciesTypes;
    this.filteredSpeciesTypes = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      //  Force noList = true if nothing to show
      this.noList = true;
      this.speciesTypesList = [];
      return;
    }
    // optional: handle scroll if user already at bottom
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  /**
   * Debounces search input changes.
   * Calls `applySearch` after a delay to prevent excessive filtering.
   *
   * @method onSearchChanged
   * @returns {void}
   */
  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch(); // debounced
    }, 300);
  }

  /**
   * Handles the submission of the Species Type form (Add or Update).
   *
   * - Validates the form.
   * - Calls `create` or `updateOneByID` service methods based on mode.
   * - Displays success notification.
   * - Refreshes the list via `ngOnInit`.
   *
   * @async
   * @method addUpdateSpeciesType
   * @returns {Promise<void>}
   * @throws {Error} If the service call fails.
   */
  public async addUpdateSpeciesType() {
    this.submitted = true;
    // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }
    try {
      this.speciesTypeDoubleClick = true;
      // GET FORM VALUES
      const formData = this.registerForm.value; // contains updated values
      if (this.submitButton) {
        await this.speciesTypeService.create(formData);
        this.toasterService.success('Species type created successfully', '', {
          timeOut: 2000
        });
      } else {
        const updateSpeciesType = { ...formData }
        delete updateSpeciesType.id;
        await this.speciesTypeService.updateOneByID(this.speciesType.id, updateSpeciesType);
        this.toasterService.success('Species type updated successfully', '', {
          timeOut: 2000
        });
      }
      await this.ngOnInit();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.speciesTypeDoubleClick = false;
    }
  }

  /**
   * Retrieves a Species Type by ID and populates the form for editing.
   * Sets the component to 'Update' mode.
   *
   * @method getSpeciesTypeById
   * @param {string} id - The ID of the species type to edit.
   * @returns {void}
   */
  public getSpeciesTypeById(id: string) {
    this.submitButton = false;
    this.showForm = true;
    this.speciesTypeDoubleClick = false;
    this.updateButton = true;
    const foundSpeciesType = this.speciesTypesList.find(uom => uom?.id === id);
    this.speciesType = foundSpeciesType ? { ...foundSpeciesType } : new SpeciesTypeModel(); // clone object
    if (this.speciesType) {
      this.registerForm.patchValue({
        culture_category_id: this.speciesType.culture_category_id,
        name: this.speciesType.name,
        abbreviation: this.speciesType.abbreviation,
        description: this.speciesType.description
      });
    }
  }

  /**
   * Resets the form and component state to default values.
   *
   * @method clear
   * @returns {void}
   */
  public clear() {
    this.registerForm.reset();
    this.showForm = false;
    this.speciesType = new SpeciesTypeModel();
    this.speciesTypeDoubleClick = false;
    this.submitButton = false;
    this.updateButton = false;
    this.submitted = false;
  }

  /**
   * Opens the form in 'Create' mode.
   *
   * @method openForm
   * @returns {void}
   */
  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    // Count form fields to toggle layout
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }
}
